import { EmeraldError } from 'src/common/api/emerald-error';
import { AbortedError } from 'src/common/api/extended-emerald-error';
import { EpsData, CardDetailsContext } from 'src/devices/payment/dtos/eps-data';
import {
  getCardDetailsFromPinPad,
  restartPaymentSession,
} from 'src/devices/payment/pinpad-operations';
import { ILineItemAddData } from 'src/items-list/business-logic/line-item-add';
import { performOnlineItemAuthorizationItemSale } from './authorize-online-items';
import { addOnlineItemByExternalId } from './business-logic/add-online-item';
import { matchBarcodeToItemId } from './business-logic/barcode-mismatch-void-item';
import {
  closeAuthorizeOnlineItemIdModal,
  showAuthorizeOnlineItemIdModal,
} from './components/authorize-online-item-id-modal';
import {
  closeSwipeCardOnlineItemModal,
  showSwipeCardOnlineItemModal,
} from './components/swipe-online-item-card-modal';
import { LookupOnlineBehavior } from './dto/LookupOnlineBehavior';
import {
  OnlineItemAuthorizationTrigger,
  OnlineItemExternalId,
} from './enumerations/OnlineItemAuthorizationTrigger';

export const handleAuthorizeOnlineItemId = async (
  lineItemData: ILineItemAddData,
  onlineItemGroup?: LookupOnlineBehavior,
): Promise<void> => {
  if (!onlineItemGroup) {
    throw new EmeraldError('FailedItemBehaviorLookup');
  }

  if (onlineItemGroup.authorizationExternalIdType === OnlineItemExternalId.Card) {
    const epsData = await processSwipeCardOnlineItem(lineItemData);
    if (!epsData || !epsData.card.pan) {
      throw new EmeraldError('OnlineItemCardNumberError');
    }

    if (onlineItemGroup.authorizationTrigger === OnlineItemAuthorizationTrigger.ItemSale) {
      return performOnlineItemAuthorizationItemSale(lineItemData, epsData.card.pan).finally(
        restartPaymentSession,
      );
    } else {
      return addOnlineItemByExternalId(lineItemData, epsData.card.pan).finally(
        restartPaymentSession,
      );
    }
  } else {
    return new Promise<void>((resolve, reject) => {
      const onClose = (): void => reject(new AbortedError());
      const onVerify = async (barcodeVerify: string): Promise<void> => {
        if (onlineItemGroup.authorizationExternalIdType === OnlineItemExternalId.InBarcode) {
          return matchBarcodeToItemId(
            lineItemData,
            barcodeVerify,
            onlineItemGroup.authorizationTrigger,
          )
            .then(resolve)
            .catch(reject);
        } else {
          return addOnlineItemByExternalId(lineItemData, barcodeVerify)
            .then(resolve)
            .catch(reject);
        }
      };

      showAuthorizeOnlineItemIdModal(
        {
          lineItemData,
          onClose,
          onVerify,
        },
        onVerify,
      );
    }).finally(closeAuthorizeOnlineItemIdModal);
  }
};

const processSwipeCardOnlineItem = async (lineItemData: ILineItemAddData): Promise<EpsData> => {
  return new Promise<EpsData>((resolve, reject) => {
    const onClose = (): void => reject(new AbortedError());
    showSwipeCardOnlineItemModal({
      lineItemData,
      onClose,
    });

    //  call async function and close promise using resolve/reject
    restartPaymentSession()
      .then(() => getCardDetailsFromPinPad(1, 1, undefined, CardDetailsContext.NonPayment))
      .then(resolve)
      .catch(() => reject(new EmeraldError('OnlineItemCardNumberError')));
  }).finally(closeSwipeCardOnlineItemModal);
};
